import React from 'react'
import { Grid, Card, CardContent, Typography, Stack, Chip, Box, Avatar } from "@mui/material"
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import UserContextP from "Interfaces/Context"
import { useContext } from "react"
import { resolveCityFromOrder, resolveStateFromOrder } from "Pages/UpdateDocument/LocationHelper"

interface PropsInterface {
    order: any
    costumer: any
    color: string
    clientAddress?: any
}

const cardStyle = {
    borderRadius: 2.5,
    border: '1px solid #e2e8f0',
    bgcolor: '#ffffff',
    boxShadow: '0 2px 8px rgba(17, 42, 86, 0.03)',
    height: '100%',
    transition: 'all 0.2s ease',
    '&:hover': {
        borderColor: '#cbd5e1',
        boxShadow: '0 4px 12px rgba(17, 42, 86, 0.06)'
    }
}

const Header = (props: PropsInterface) => {
    const { order, costumer, clientAddress } = props
    const context = useContext(UserContextP)

    const currency = process.env.REACT_APP_CURRENCY ?? '$'
    const formatMoney = (value: number) => `${currency} ${context.separatorMiles(value ?? 0)}`

    const balance = costumer?.Balance ?? 0
    const creditLine = costumer?.CreditLine ?? 0
    const available = creditLine - balance

    const cityName = resolveCityFromOrder(order, clientAddress, costumer)
    const stateName = resolveStateFromOrder(order, clientAddress, costumer)
    const streetText = (order?.street && String(order.street).trim() !== '')
        ? String(order.street).trim()
        : (clientAddress?.street && String(clientAddress.street).trim() !== ''
            ? String(clientAddress.street).trim()
            : (costumer?.Address && String(costumer.Address).trim() !== ''
                ? String(costumer.Address).trim()
                : 'No posee dirección'))

    return (
        <>
            {/* Company / Commercial name */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={cardStyle}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Avatar sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', width: 40, height: 40, borderRadius: 2 }}>
                                <BusinessOutlinedIcon fontSize="small" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: '#64748b' }}>
                                    Razón Social
                                </Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mt: 0.2, lineHeight: 1.3 }}>
                                    {costumer?.CardName || order?.cardName || '—'}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.8 }}>
                                    <Chip
                                        label={`Cód: ${costumer?.CardCode || order?.cardCode || '—'}`}
                                        size="small"
                                        sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: '11px', height: 22, borderRadius: '50px' }}
                                    />
                                </Stack>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={cardStyle}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Avatar sx={{ bgcolor: '#f5f3ff', color: '#7c3aed', width: 40, height: 40, borderRadius: 2 }}>
                                <StorefrontOutlinedIcon fontSize="small" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: '#64748b' }}>
                                    Nombre Comercial
                                </Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mt: 0.2, lineHeight: 1.3 }}>
                                    {costumer?.CardFName || costumer?.CardName || order?.cardFName || order?.cardName || '—'}
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            {/* Financial summary: balance and credit */}
            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={cardStyle}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Avatar sx={{ bgcolor: '#fff7ed', color: '#ea580c', width: 40, height: 40, borderRadius: 2 }}>
                                <AccountBalanceWalletOutlinedIcon fontSize="small" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: '#64748b' }}>
                                    Saldo pendiente
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mt: 0.6, gap: 0.5 }}>
                                    <Chip
                                        label={formatMoney(balance)}
                                        size="small"
                                        sx={{ bgcolor: '#fff1f2', color: '#be123c', fontWeight: 700, fontSize: '12px', height: 24, borderRadius: '50px' }}
                                    />
                                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                                        Disponible: <strong style={{ color: available >= 0 ? '#15803d' : '#b91c1c' }}>{formatMoney(available)}</strong>
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} md={6}>
                <Card elevation={0} sx={cardStyle}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Avatar sx={{ bgcolor: '#ecfdf5', color: '#059669', width: 40, height: 40, borderRadius: 2 }}>
                                <CreditCardOutlinedIcon fontSize="small" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: '#64748b' }}>
                                    Límite de crédito
                                </Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#047857', mt: 0.2 }}>
                                    {formatMoney(creditLine)}
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
                <Card elevation={0} sx={cardStyle}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Avatar sx={{ bgcolor: '#f1f5f9', color: '#475569', width: 40, height: 40, borderRadius: 2 }}>
                                <LocationOnOutlinedIcon fontSize="small" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: '#64748b' }}>
                                    Dirección de entrega
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600, mt: 0.2 }}>
                                    {streetText}
                                </Typography>

                                {/* Ciudad y detalles de entrega */}
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.8, gap: 0.8 }}>
                                    {cityName && (
                                        <Chip
                                            icon={<LocationCityOutlinedIcon sx={{ fontSize: '16px !important', color: '#047857 !important' }} />}
                                            label={`Ciudad: ${cityName}${stateName && stateName.toLowerCase() !== cityName.toLowerCase() ? ` (${stateName})` : ''}`}
                                            size="small"
                                            sx={{
                                                bgcolor: '#f0fdf4',
                                                border: '1.5px solid #86efac',
                                                color: '#15803d',
                                                fontWeight: 700,
                                                fontSize: '12px',
                                                height: 26,
                                                borderRadius: '50px',
                                                px: 0.5
                                            }}
                                        />
                                    )}
                                    {(order?.payToCode || order?.address || clientAddress?.address) && (
                                        <Chip
                                            label={`Sucursal: ${typeof order?.payToCode === 'string'
                                                ? order.payToCode
                                                : (order?.payToCode?.location || order?.address || clientAddress?.address || '')}`}
                                            size="small"
                                            sx={{
                                                bgcolor: '#f1f5f9',
                                                color: '#475569',
                                                fontWeight: 600,
                                                fontSize: '11px',
                                                height: 24,
                                                borderRadius: '50px'
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>
        </>
    )
}

export default Header